import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import { isMobileDevice } from "@MasterBluePrint/Overrides/Common/Utilities/isMobileDevice";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface CheckoutReviewAndSubmitPageContainerStyles {
    loadingOverlay?: LoadingOverlayProps;
    container?: GridContainerProps;
    headerGridItem?: GridItemProps;
    approverInfoGridItem?: GridItemProps;
    promoCodeAndCartTotalGridItem?: GridItemProps;
    promoCodeAndCartTotalContainer?: GridContainerProps;
    promoCodeGridItem?: GridItemProps;
    cartTotalGridItem?: GridItemProps;
    paymentDetailsAndCreditCardDetailsAndAddressGridItem?: GridItemProps;
    productListWrapper?: GridItemProps;
    paymentDetailsAndCreditCardDetailsAndAddressContainer?: GridContainerProps;
    paymentDetailsGridItem?: GridItemProps;
    TaxDetailsGridItem?: GridItemProps;
    creditCardDetailsGridItem?: GridItemProps;
    creditCardAddressGridItem?: GridItemProps;
    shippingInfoAndProductListGridItem?: GridItemProps;
    shippingInfoAndProductListContainer?: GridContainerProps;
    hiddenShippingInfoAndProductListWide?: HiddenProps;
    shippingInfoGridItemWide?: GridItemProps;
    productListGridItemWide?: GridItemProps;
    hiddenShippingInfoAndProductListNarrow?: HiddenProps;
    shippingInfoGridItemNarrow?: GridItemProps;
    productListGridItemNarrow?: GridItemProps;
    actionButtonsBottomGridItem?: GridItemProps;
    productSubtotalBottom?: GridItemProps;
}

export const pageContainerStyles: CheckoutReviewAndSubmitPageContainerStyles = {
    loadingOverlay: {
        css: css`
            width: 100%;
        `,
    },
    container: {
        css: css`

            &.page-container {
                max-width: 100%;
            }

            h2.section-heading,
            h3.section-heading {
                color: ${themeColors.colorPrimary};
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size20};
                font-weight: ${themeFontWeights.fontWeightForBold};
                letter-spacing: 0;
                line-height: 1.375rem;
                margin-bottom: 1.25rem;
            }
            section.price-section > div {
                align-items:baseline;
            }
            section.price-section > div span {
                font-family: Roboto, sans-serif;
                font-size:.875rem;
                letter-spacing: -0.38px;
            }
            section.price-section > div span:nth-of-type(1) {
                font-size:1.25rem;
            }
            div.part-number-container span {font-size:.875rem !important;}
            div.mfg-part-container span {font-size:.875rem !important;}
            @media (min-width: 991px) {
                div[data-test-selector="cardLinesList_current"] {
                    > div {
                        padding-left: 2.8125rem;
                        padding-right: 1.5625rem;
                    }
                }
                &.page-container {
                    padding-left: 0.625rem;
                }
            }

        `,
    },
    headerGridItem: { width: 12 },
    approverInfoGridItem: {
        width: 12,
        css: css`
            padding: 0 .9375rem;
        `,
    },
    promoCodeAndCartTotalGridItem: {
        width: [12, 12, 12, 4, 3],
        css: css`
            @media screen and (min-width: 991px){
                &.checkout-sidebar {
                    margin-top: 2.8125rem !important;
                    padding-left: 0;
                    padding-right: 0;
                }

                .adjust-top {
                    position: fixed;
                    top: 0;
                    width: 19.5rem;
                }
                .bottom-reached {
                    top: auto;
                    bottom: 0;
                    position: absolute;
                    width: 19.5rem;
                }
                
            }
            

            @media screen and (min-width: 1400px){
                .adjust-top {
                    width: 20.4375rem;
                }
                .bottom-reached {
                    width: 20.4375rem;
                }
            }

            .cart-total {
                padding: 0;
                background: transparent !important;
            }

            button[data-test-selector="checkoutReviewAndSubmit_placeOrder"] {
                margin-top: 1.5625rem;
                text-transform: uppercase;
                > span {
                    font-size: ${themeFontSizes.size14};
                }
            }

            .side-wrapper {
                background-color: ${themeColors.colorWhite};
                border-top: 0.0625rem solid ${themeColors.colorBorderBrands};
                padding-top: 0.625rem;
                margin-top: .9375rem;
                width: 100%;

                @media screen and (min-width: 1200px) {
                    > div {
                        > div[class^="GridOffset"], 
                        > div[class*=" GridOffset"] {
                            max-width: calc(100% + 0.625rem) !important;
                        }
                    }
                }
                            
                .checkout-tooltip-content {
                    &:after {
                        right: 7.3125rem;
                    }
                }

                .border-seperator {
                    border-bottom: 0.0625rem solid ${themeColors.colorBorderBrands};
                    padding-top: .9375rem;
                    margin-bottom: .9375rem;
                    width: 100%;
                    display: block;
                }
                .grid-container {
                    padding-left: 0 !important;
                    padding-right: 0 !important;

                    > div {
                        div:nth-child(odd) {
                            > span {
                                color: ${themeColors.colorGunsmoke};
                                font-family: Roboto, sans-serif;
                                font-size: ${themeFontSizes.size16};
                                font-weight: ${themeFontWeights.fontWeightRegular};
                                letter-spacing: -0.0194rem;
                                line-height: 1.4375rem;

                                &.total-value {
                                    opacity: 0.7;
                                    color: ${themeColors.colorDanger};
                                    font-family: 'Roboto', sans-serif;
                                    font-size: ${themeFontSizes.size22};
                                    font-weight: ${themeFontWeights.fontWeightForBold};
                                    letter-spacing: -0.0262rem;
                                    line-height: 1.4375rem;
                                    text-align: right;
                                }
                            }
                            > span[data-test-selector="cartTotal_subTotal"] {
                                font-weight: ${themeFontWeights.fontWeightForBold};
                            }
                            .total-label {
                                color: ${themeColors.colorCharcoalGrey};
                                font-family: Roboto, sans-serif;
                                font-size: ${themeFontSizes.size20};
                                font-weight: ${themeFontWeights.fontWeightMedium};
                                letter-spacing: -0.0238rem;
                                line-height: 1.4375rem;
                                text-align: left !important;
                                font-weight: ${themeFontWeights.fontWeightMedium};
                            }
                        }
                        div:nth-child(even) {
                            text-align: right;
                            > span {
                                color: ${themeColors.colorCharcoalGrey};
                                font-family: 'Roboto', sans-serif;
                                font-size: ${themeFontSizes.size20};
                                letter-spacing: -0.0238rem;
                                line-height: 1.4375rem;

                                &.size-adjust {
                                    font-size: ${themeFontSizes.size16};
                                    letter-spacing: -.019rem;
                                    font-weight: 400;
                                }
                            }
                            span[data-test-selector="cartTotalShippingAndHandlingAmount"] {
                                font-weight: ${themeFontWeights.fontWeightRegular};
                                span {
                                    font-weight: ${themeFontWeights.fontWeightRegular};
                                }
                            }
                            > span[data-test-selector="cartTotal_subTotal"] {
                                font-weight: ${themeFontWeights.fontWeightForBold};
                            }

                            .total-label {
                                font-weight: ${themeFontWeights.fontWeightMedium};
                            }
                        }
                    }
                }
                @media print {
                    display: none;
                }
            }
            @media screen and (max-width: 991px){
                .side-wrapper {
                    padding-top: 1.5625rem;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            order: 1;
                            button[data-test-selector="checkoutReviewAndSubmit_placeOrder"] {
                                margin-top: 1.5625rem;
                                width: 100%;
                            }
                        `,
                        css`
                            order: 1;
                        `,
                        css`
                            order: 1;
                        `,
                        css`
                            order: 2;
                            button[data-test-selector="checkoutReviewAndSubmit_placeOrder"] {
                                margin-top: 2.1875rem;
                            }
                        `,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    promoCodeAndCartTotalContainer: {
        css: css`
            padding: 1.4375rem 1.5rem 2.8125rem 1.5rem;
            background: ${themeColors.colorWhite};
            box-shadow: 0 0.125rem .250625rem .250625rem rgba(0,0,0,.03);
            border-radius: 0.125rem;

            > div {
                width: 100% !important;
                margin: 0;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 1.4375rem 1.5rem 2.8125rem 1.5rem;
                        `,
                        null,
                        null,
                        css`
                            padding: 1.4375rem 1.5rem 5.3125rem 1.5rem;
                        `,
                        null,
                    ],
                    "min",
                )}
        `
    },
    promoCodeGridItem: { 
        width: 12,
        css: css`
            padding: 0;
            background: transparent;

            @media screen and (min-width: 1200px){
                width: 100%;

                div[class^='GridOffset'], div[class*=' GridOffset'] {
                    max-width: 100% !important;
                }
            }
        `
     },
    cartTotalGridItem: { 
        width: 12,
        css: css`
            padding: 0;
            background: transparent;

            > div > div > div {
                width: calc(100% + 0.3125rem) !important;

                @media screen and (max-width: 990px) {
                    > div {
                        display: block;
                    }
                }
            }
        `
     },
    paymentDetailsAndCreditCardDetailsAndAddressGridItem: {
        width: [12, 12, 12, 8, 9],
        css: css`
                div.part-number-container {
                    span {font-size:.875rem;}
                    span:nth-of-type(2) {
                        display:inline-block;
                    }
                }
                div.mfg-part-container {
                    span {font-size:.875rem;}
                    span:nth-of-type(2) {
                        display:inline-block;
                    }
                }
                div.quantity-container p[data-test-selector="heading"] {
                    display:none;
                }
                div.quantity-container p[data-test-selector="text"] {
                    display:inline-block;
                }
                @media (max-width: 991px){
                    padding-top: 1.5625rem;
                }

                @media (max-width: 767px){
                    div.quantity-container p[data-test-selector="heading"],
                    div.quantity-container p[data-test-selector="text"] {
                        display:inline-block;
                    }
                    .expanded-view {
                        background: ${themeColors.colorWhite};
                        margin-bottom: .9375rem;
                        border-bottom: .250625rem solid ${themeColors.colorBorderBrands};
                        padding-left: .9375rem;
                        padding-right: .9375rem;
                    }
                    h3.products-heading {
                        padding-left: .9375rem;
                    }
                    .price-container {
                        padding-bottom: 1.2rem;
    
                        p[data-test-selector="heading"] {
                            display: none;
                        }
    
                        p[data-test-selector="text"] {
                            opacity: 0.7;
                            color: ${themeColors.colorDanger};
                            font-family: 'Roboto', sans-serif;
                            font-size: ${themeFontSizes.size20};
                            font-weight: ${themeFontWeights.fontWeightForBold};
                            letter-spacing: -0.0238rem;
                            line-height: 1.4375rem;
                        }
                    }

                    div.part-number-container {
                        order: 2 !important;    
                        padding-top: 0;
                        margin-bottom: 0.3125rem;
    
                        span {
    
                            &:first-child {
                                margin-bottom: .125rem;
                            }
                        }
                    }
                    div.mfg-part-container {
                        order: 1 !important;    
                        span {
    
                            &:first-child {
                                margin-bottom: .125rem;
                            }
                        }
                    }
                
                }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            order: 2;
                        `,
                        css`
                            order: 2;
                        `,
                        css`
                            order: 2;
                        `,
                        css`
                            order: 1;
                        `,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    productListWrapper: {
        width: [12, 12, 12, 8, 9],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            order: 4;
                        `,
                        null,
                        css`
                            order: 4;
                        `,
                        null,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    paymentDetailsGridItem: { width: 12 },
    TaxDetailsGridItem: { width: 12 },
    creditCardDetailsGridItem: { width: [12, 12, 12, 6, 6] },
    creditCardAddressGridItem: { width: [12, 12, 12, 6, 6] },
    shippingInfoAndProductListGridItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            order: 3;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    hiddenShippingInfoAndProductListWide: {
        below: "lg",
        css: css`
            width: 100%;
        `,
    },
    shippingInfoGridItemWide: {
        width: 12,
    },
    productSubtotalBottom: {
        width: 12,
        css: css`
            display: block;
            padding-top: 0;

            > div {
                max-width: 25rem;
                float: right;
                display: block;
                > div > div {
                    width: calc(100% + 0.625rem) !important;
                }
                @media screen and (min-width: 991px){
                    margin-right: 1.5625rem;
                }

                @media screen and (max-width: 990px) {
                    > div > div {
                        > div {
                            display: block;
                        }
                    }
                    max-width: 100%;
                    float: none;
                    background: ${themeColors.colorWhite};
                    padding: 1.4375rem 1.5rem 3.125rem 1.5rem;
                    box-shadow: 0 0.125rem .250625rem .250625rem rgb(0 0 0 / 3%);
                    border-radius: 0.125rem;
                }
            }
            @media screen and (min-width: 991px) {
                button[data-test-selector="checkoutReviewAndSubmit_placeOrder"] {
                    display: none;
                }
            }

            @media screen and (max-width: 990px) {
                button[data-test-selector="checkoutReviewAndSubmit_placeOrder"] {
                    display: block;
                    margin-top: 1.5625rem;
                    width: 100%;
                }
            }

            .side-wrapper {
                .border-seperator {
                    border-bottom: 0.0625rem solid ${themeColors.colorBorderBrands};
                    padding-top: .9375rem;
                    margin-bottom: 1.25rem;
                    width: 100%;
                    display: block;
                }
                .grid-container {
                    padding-left: 0 !important;
                    padding-right: 0 !important;
    
                    > div {
                        div:nth-child(odd) {
                            > span {
                                color: ${themeColors.colorGunsmoke};
                                font-family: Roboto, sans-serif;
                                font-size: ${themeFontSizes.size16};
                                font-weight: ${themeFontWeights.fontWeightMedium};
                                letter-spacing: -0.0194rem;
                                line-height: 1.4375rem;
    
                                &.total-value {
                                    opacity: 0.7;
                                    color: ${themeColors.colorDanger};
                                    font-family: 'Roboto', sans-serif;
                                    font-size: ${themeFontSizes.size22};
                                    font-weight: ${themeFontWeights.fontWeightForBold};
                                    letter-spacing: -0.0262rem;
                                    line-height: 1.4375rem;
                                    text-align: right;
                                }
                            }
                            > span[data-test-selector="cartTotal_subTotal"] {
                                font-weight: ${themeFontWeights.fontWeightForBold};
                            }
                            .total-label {
                                color: ${themeColors.colorCharcoalGrey};
                                font-family: Roboto, sans-serif;
                                font-size: ${themeFontSizes.size20};
                                font-weight: ${themeFontWeights.fontWeightMedium};
                                letter-spacing: -0.0238rem;
                                line-height: 1.4375rem;
                                text-align: left !important;
                            }
                        }
                        div:nth-child(even) {
                            text-align: right;
                            > span {
                                color: ${themeColors.colorCharcoalGrey};
                                font-family: 'Roboto', sans-serif;
                                font-size: ${themeFontSizes.size20};
                                letter-spacing: -0.0238rem;
                                line-height: 1.4375rem;
                            }
                            > span[data-test-selector="cartTotal_subTotal"] {
                                font-weight: ${themeFontWeights.fontWeightForBold};
                            }
                        }
                    }
                }
            }
        `,
    },
    productListGridItemWide: {
        width: 12,
        css: css`
            h3.products-heading {
                font-size: ${themeFontSizes.size20};
            }
            .quantity-container {
                padding-top: .250625rem;
                padding-bottom: .250625rem;
                border-top: 0.0625rem solid ${themeColors.colorLavenderPinocchio};
                border-bottom: 0.0625rem solid ${themeColors.colorLavenderPinocchio};
                order: 0;
                > div > div > div:nth-child(2n) {
                    p[data-test-selector="heading"] {
                        font-size: ${themeFontSizes.size14};
                        margin-right: .5rem;

                        &:after {
                            content: none;
                        }
                    }
                    > div > div {
                         flex-direction: row !important;
                    }
                    p[data-test-selector="text"] {
                        font-size: ${themeFontSizes.size14};
                    }
                    p[data-test-selector="heading"] {
                        color: ${themeColors.colorPrimary};
                        letter-spacing: 0.4.250625rem;
                        min-width:fit-content !important;
                    }
                }
                ~ div {
                    order: 2;
                }
            }
            @media screen and (max-width: 767px) {
                h3.products-heading {
                    padding-left: .9375rem;
                }
                .expanded-view {
                    background: ${themeColors.colorWhite};
                    border-bottom: .250625rem solid ${themeColors.colorBorderBrands};
                    margin-bottom: 1.25rem;
                    padding: 2.5rem 0.75rem 0 0.75rem;
                }
                .price-container {
                    padding-bottom: 1.2rem;
                    display: none;

                    p[data-test-selector="heading"] {
                        display: none;
                    }

                    p[data-test-selector="text"] {
                        opacity: 0.7;
                        color: ${themeColors.colorDanger};
                        font-family: 'Roboto', sans-serif;
                        font-size: ${themeFontSizes.size20};
                        font-weight: ${themeFontWeights.fontWeightForBold};
                        letter-spacing: -0.0238rem;
                        line-height: 1.4375rem;
                    }
                }
                .quantity-container {
                    padding-top: .250625rem;
                    padding-bottom: .250625rem;
                    border-top: 0.0625rem solid ${themeColors.colorLavenderPinocchio};
                    border-bottom: 0.0625rem solid ${themeColors.colorLavenderPinocchio};
                    order: 0;
                    > div > div > div:nth-child(2n) {
                        p[data-test-selector="heading"] {
                            font-size: ${themeFontSizes.size14};
                            margin-right: .5rem;
    
                            &:after {
                                content: none;
                            }
                        }
                        p[data-test-selector="text"] {
                            font-size: .875rem
                        }
                        p[data-test-selector="heading"] {
                            letter-spacing: 0.4.250625rem;
                            font-size: .875rem
                        }
                        > div > div {
                            flex-direction: row !important;
                        }
                    }
                    ~ div {
                        order: 2;
                    }
                }
                div.part-number-container {
                    margin-top: 0.625rem;
                    padding-top: 0.625rem;
                    border-top: 0.0625rem solid ${themeColors.colorLavenderPinocchio};
                    margin-bottom: 0.3125rem;

                    span {
                        display: inline-block !important;
                        font-size: .875rem !important;

                        &:first-child {
                            margin-bottom: .125rem;
                            color: ${themeColors.colorPrimary};
                            letter-spacing: 0.4.250625rem;
                        }
                    }
                }
                div.mfg-part-container {    
                    span {
                        display: inline-block !important;
                        font-size: .875rem !important;

                        &:first-child {
                            margin-bottom: .125rem;
                            color: ${themeColors.colorPrimary};
                            letter-spacing: 0.4.250625rem;
                        }
                    }
                }
                .each-price-container {
                    order: 1;
                    padding-bottom: 2.2rem;
                    padding-top: .625rem;
                    .price-section {
                        legend {
                            display: none;
                        }
                        > div {
                            span[data-test-selector="productPrice_unitNetPrice"] {
                                opacity: 0.7;
                                color: ${themeColors.colorDanger};
                                font-family: 'Roboto', sans-serif;
                                font-size: ${themeFontSizes.size20};
                                font-weight: ${themeFontWeights.fontWeightForBold};
                                letter-spacing: -0.0238rem;
                                line-height: 1.4375rem;
                            }
                        }
                    }
                }
            }
        `,
    },
    hiddenShippingInfoAndProductListNarrow: {
        above: "md",
        css: css`
            width: 100%;
        `,
    },
    shippingInfoGridItemNarrow: {
        width: 12,
    },
    productListGridItemNarrow: {
        width: 12,
    },
    actionButtonsBottomGridItem: {
        css: css`
            order: 4;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
        width: 12,
    },
};

const styles = pageContainerStyles;
const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const location = getLocation(state);
    const currentPageUrl = location.pathname;

    return {
        isPreloadingData:
            state.pages.orderConfirmation.isPreloadingData ||
            state.pages.checkoutShipping.isPreloadingData ||
            state.pages.checkoutReviewAndSubmit.isPreloadingData ||
            cartState.isLoading,
        isPlacingOrder: state.pages.checkoutReviewAndSubmit.isPlacingOrder,
        currentPageUrl,
    };
};


type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const CheckoutReviewAndSubmitPageContainer: FC<Props> = ({ id, isPreloadingData, isPlacingOrder, currentPageUrl }) => {
    const checkScrollTop = () => {
        const checkoutSidebarElement = (document.querySelectorAll('div[data-test-selector="checkout-total-container"]')[0]  as HTMLElement);
        const checkoutSidebarElementParent = (document.querySelectorAll('div[data-test-selector="checkout-sidebar"]')[0]  as HTMLElement);
        const footerElement = (document.querySelectorAll('footer')[0]  as HTMLElement);
        const checkoutSidebarElementHeight = checkoutSidebarElement?.offsetHeight;
        const checkoutSidebarElementParentWidth  = checkoutSidebarElementParent?.clientWidth;
    
        const checkoutSidebarElementPageElement = window.pageYOffset + checkoutSidebarElementHeight;
        const windowHeight = document.body.scrollHeight;
        const footerElementHeight = footerElement.offsetHeight + 30;
        const windowHeightExcFooter = windowHeight - footerElementHeight;
        
        if (checkoutSidebarElement && (window.pageYOffset > 265) && (checkoutSidebarElementPageElement <= windowHeightExcFooter)) {
            checkoutSidebarElement.classList.add('adjust-top');
            checkoutSidebarElement.style.width = `${checkoutSidebarElementParentWidth  }px`;
            checkoutSidebarElement.classList.remove('bottom-reached');
        } else if (checkoutSidebarElement && (checkoutSidebarElementPageElement >= windowHeightExcFooter)) {
            checkoutSidebarElement.classList.add('bottom-reached');
            checkoutSidebarElement.style.width = `${checkoutSidebarElementParentWidth  }px`;
            checkoutSidebarElement.classList.remove('adjust-top');
        } else if (checkoutSidebarElement && (window.pageYOffset < 265)) {
            checkoutSidebarElement.classList.remove('bottom-reached');
            checkoutSidebarElement.classList.remove('adjust-top');
            checkoutSidebarElement.style.width = '';
        }
    };
    
    if (typeof window !== "undefined" && typeof document !== "undefined" && currentPageUrl.toLowerCase().includes("checkoutreview")) {
        window.addEventListener("scroll", checkScrollTop);
    }

    let featureFlags: string[] = ["TEUS-7945"];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const enablePlaceOrderButtonAtBottomOfCheckout = flagValues["TEUS-7945"];
    
    return (
        <LoadingOverlay {...styles.loadingOverlay} loading={isPreloadingData || isPlacingOrder}>
            <GridContainer {...styles.container} className="page-container">
                <GridItem {...styles.headerGridItem}>
                    <Zone zoneName="Content00" contentId={id} />
                </GridItem>
                <GridItem {...styles.approverInfoGridItem}>
                    <Zone zoneName="Content08" contentId={id} />
                </GridItem>
                <GridItem {...styles.paymentDetailsAndCreditCardDetailsAndAddressGridItem} className="checkout-shipping-container">
                    <GridContainer>
                        <GridItem {...styles.paymentDetailsGridItem}>
                            <Zone zoneName="Content03" contentId={id} />
                        </GridItem>
                    </GridContainer>
                </GridItem>
                {/* <GridItem {...styles.productListWrapper}>
                    <GridContainer>
                        <GridItem {...styles.productSubtotalBottom}>
                            <Zone zoneName="Content09" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.productListGridItemWide}>
                            <Zone zoneName="Content10" contentId={id} />
                        </GridItem>
                    </GridContainer>
                </GridItem> */}
                <GridItem 
                    {...styles.promoCodeAndCartTotalGridItem}
                    data-test-selector="checkout-sidebar"
                    className="checkout-sidebar"
                    style={
                        isMobileDevice() && enablePlaceOrderButtonAtBottomOfCheckout
                            ? { ...(styles.promoCodeAndCartTotalGridItem?.style || {}), order: 3 }
                            : styles.promoCodeAndCartTotalGridItem?.style
                    }
                >
                    <GridContainer {...styles.promoCodeAndCartTotalContainer} data-test-selector="checkout-total-container"  className="checkout-total-container">
                        <GridItem {...styles.promoCodeGridItem}>
                            <Zone zoneName="Content01" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.cartTotalGridItem}>
                            <Zone zoneName="Content02" contentId={id} />
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CheckoutReviewAndSubmitPageContainer),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        displayName: "Page Container",
    },
};

export default widgetModule;
